// @ts-nocheck
import { useCallback, useEffect, useState } from "react";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { resolveDocumentName } from "@features/port-security/components/utils";
import { useAzureBlobUpload } from "@features/port-security/hooks/useAzureBlobUpload";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import type { PortServicesOutletContext } from "../PortServicesContainer";
import {
  type PortServicesDocument,
  type PortServicesFormData,
} from "../store/usePortServicesForm.store";

export const samePortValidation = (vesselPortCode?: string, selectedPortCode?: string): boolean => {
  const normalizedVesselPortCode = vesselPortCode?.trim().toUpperCase();
  const normalizedSelectedPortCode = selectedPortCode?.trim().toUpperCase();

  if (!normalizedVesselPortCode || !normalizedSelectedPortCode) {
    return true;
  }

  return normalizedVesselPortCode === normalizedSelectedPortCode;
};

// This list will be extended as we add more port service categories approval flows
export const SERVICE_CATEGORIES_WITH_EXTRA_FIELDS = [
  "Waste Disposal",
  "Bunkering",
  "HSE",
  "De-bunkering",
  "De-sloping",
];

// Each port service approval flow will have it's own mapping of category and types
export const WASTE_DISPOSAL_MAP = {
  CATEGORY: "Waste Disposal",
  GARBAGE: "Garbage",
  SLOPS: "Slops",
};
export const BUNKERING_MAP = {
  CATEGORY: "Bunkering",
};
export const DEBUNKERING_MAP = {
  CATEGORY: "De-bunkering",
};

export const DE_SLOPING_MAP = {
  CATEGORY: "De-sloping",
  DEFAULT_NOTIFY_ROLE: "Marine Operator",
} as const;

export const SHIP_CHANDLING = {
  CATEGORY: "Ship Chandling",
  LIFEBOATS_AND_FENDERS: "Lifeboats and fenders, Marine others",
  LOADING_AND_OFFLOADING: "Loading & Offloading of Spares, Stores, Lube Oil and General Items",
  DEFAULT_NOTIFY_ROLE: "Dry Bulk",
} as const;

const SHIP_CHANDLING_SERVICE_TYPES = [
  SHIP_CHANDLING.LIFEBOATS_AND_FENDERS,
  SHIP_CHANDLING.LOADING_AND_OFFLOADING,
] as const;

export const HSE = {
  CATEGORY: "HSE",
  TYPE_WASTE_DISPOSAL: "Waste Disposal",
  SLOPS: "Slops",
  SLUDGE: "Sludge",
};

export const SHIP_TO_SHIP = {
  LABEL: "Ship to Ship",
  SHORT_LABEL: "STS",
  REQUIRED_NOTIFY_ROLES: ["Marine Operator", "Marine Supervisor"],
  OTHER_VESSEL_ROLE: "SECONDARY",
  DOCUMENT_TYPES: {
    SIRE_REPORT: "SIRE_REPORT",
    BILL_OF_LADING: "BILL_OF_LADING",
    CARGO_MANIFEST: "CARGO_MANIFEST",
    CERTIFICATE_OF_ORIGIN: "CERTIFICATE_OF_ORIGIN",
    HOSE_CERTIFICATE: "HOSE_CERTIFICATE",
    OTHER_DOCUMENTS: "OTHER_DOCUMENTS",
  },
} as const;

const SHIP_TO_SHIP_DOCUMENT_FIELDS = new Set<string>([
  "sireReportDocuments",
  "billOfLadingDocuments",
  "cargoManifestDocuments",
  "certificateOfOriginDocuments",
  "hoseCertificateDocuments",
  "otherDocuments",
]);

const normalizeShipToShipValue = (value?: string) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const isShipToShipService = (
  serviceCategory?: string,
  serviceType?: string,
  serviceSubType?: string,
) => {
  const candidates = [serviceCategory, serviceType, serviceSubType].map(normalizeShipToShipValue);
  return candidates.some(
    (value) => value === "ship to ship" || value === "ship to ship service" || value === "sts",
  );
};

export const isShipChandlingLifeboatsService = (serviceCategory?: string, serviceType?: string) => {
  const normalizedServiceCategory = normalizeShipToShipValue(serviceCategory);

  if (normalizedServiceCategory !== normalizeShipToShipValue(SHIP_CHANDLING.CATEGORY)) {
    return false;
  }

  if (!normalizeShipToShipValue(serviceType)) {
    return true;
  }

  const normalizedServiceType = normalizeShipToShipValue(serviceType);

  return SHIP_CHANDLING_SERVICE_TYPES.some(
    (candidate) => normalizeShipToShipValue(candidate) === normalizedServiceType,
  );
};

export const isShipChandlingCategory = (serviceCategory?: string) => {
  return (
    normalizeShipToShipValue(serviceCategory) === normalizeShipToShipValue(SHIP_CHANDLING.CATEGORY)
  );
};

export const isShipChandlingLifeboatsWorkflowService = (
  serviceCategory?: string,
  serviceType?: string,
) => {
  return (
    normalizeShipToShipValue(serviceCategory) ===
      normalizeShipToShipValue(SHIP_CHANDLING.CATEGORY) &&
    normalizeShipToShipValue(serviceType) ===
      normalizeShipToShipValue(SHIP_CHANDLING.LIFEBOATS_AND_FENDERS)
  );
};

type NotifyRoleContext = {
  serviceCategory?: string;
  serviceType?: string;
  serviceSubType?: string;
};

export const getDefaultNotifyRoles = ({
  serviceCategory,
  serviceType,
  serviceSubType,
}: NotifyRoleContext): string[] => {
  if (isShipToShipService(serviceCategory, serviceType, serviceSubType)) {
    return [...SHIP_TO_SHIP.REQUIRED_NOTIFY_ROLES];
  }

  if (isShipChandlingLifeboatsService(serviceCategory, serviceType)) {
    return [SHIP_CHANDLING.DEFAULT_NOTIFY_ROLE];
  }

  //WASTE-DISPOSAL-GARBAGE
  if (serviceType === WASTE_DISPOSAL_MAP.GARBAGE) {
    return ["Dry Bulk"];
  }

  //HSE-SLUDGE/SLOPS
  if (serviceSubType === HSE.SLUDGE || serviceSubType === HSE.SLOPS) {
    return ["HSE"];
  }

  //BUNKERING
  if (serviceCategory === BUNKERING_MAP.CATEGORY) {
    return ["Marine Operator", "Marine Supervisor"];
  }

  //DE-BUNKERING
  if (serviceCategory === DEBUNKERING_MAP.CATEGORY) {
    return ["Marine Operator"];
  }

  //DE-SLOPING
  if (serviceCategory === DE_SLOPING_MAP.CATEGORY) {
    return [DE_SLOPING_MAP.DEFAULT_NOTIFY_ROLE];
  }

  //WASTE-DISPOSAL-SLOPS
  if (serviceType === WASTE_DISPOSAL_MAP.SLOPS) {
    return ["Marine Operator", "Marine Supervisor"];
  }

  return [];
};

export const getMissingNotifyRolesMessage = (
  context: NotifyRoleContext,
  selectedNotifyRoles: string[],
): string | undefined => {
  const missingRoles = getDefaultNotifyRoles(context).filter(
    (role) => !selectedNotifyRoles.includes(role),
  );

  if (!missingRoles.length) {
    return undefined;
  }

  return t("vessel-call.port-services.fields.missing-notify-roles", {
    roles: missingRoles.join(", "),
  });
};
export const serviceSubtypeParticularities = {
  Garbage: {
    dropdownField: "garbageCategory",
    labelKey: "fields.garbage-category",
    options: [
      { label: "CATEGORY A - Plastics", value: "CATEGORY A - Plastics" },
      {
        label: "CATEGORY B - Food Waste not comminuted",
        value: "CATEGORY B - Food Waste not comminuted",
      },
      {
        label: "CATEGORY C - Paper, Glass, Metal, Rags, Bottles, Crockery",
        value: "CATEGORY C - Paper, Glass, Metal, Rags, Bottles, Crockery",
      },
      { label: "CATEGORY E - Incinerator Ashes", value: "CATEGORY E - Incinerator Ashes" },
      { label: "CATEGORY F - Operational Waste", value: "CATEGORY F - Operational Waste" },
    ],
    hasLocationField: true,
    testId: "garbage-category",
    uploadFieldName: "garbageDocument",
    uploadTitle: "master-declaration",
  },
  Slops: {
    testId: "slops",
    hasLocationField: false,
    uploadFieldName: "slopsDocument",
    uploadTitle: "oily-waste-delivery-form-onshore",
    fileType: "OILY_WASTE_DELIVERY_FORM_ONSHORE",
  },
  Sludge: {
    hasLocationField: false,
    testId: "type-of-sludge",
    uploadFieldName: "sludgeDocument",
    uploadTitle: "advance-notification-form",
  },
  Debunkering: {
    testId: "debunkering",
    hasLocationField: false,
    uploadFileName: "debunkeringDocument",
    uploadTitle: "bunker-delivery-note",
    fileType: "BUNKER_DELIVERY_NOTE",
  },
} as any;

export interface UploadItem {
  /** Original file name – used for display */
  name?: string;
  /** URL for the document (blob URL) */
  url?: string;
  /** Stored file MIME type */
  type?: string;
  /** Certificate / document type */
  certificateType?: string;
  /** Original file name stored separately (if needed) */
  originalFileName?: string;
  documentType?: string;
  otherSpecifyType?: string;
  documentSummary?: string;
  issueDate?: Date | string | null;
  /** Expiration date from store or form */
  expirationDate?: Date | string | null;
  inspectionDate?: Date | string | null;
  /** Index hint returned from upload flow */
  idx?: number;
}

export const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
export const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "pdf"];
const ADDITIONAL_DOCUMENT_TYPE = "ADDITIONAL_DOCUMENT";

const normalizeDocumentKind = (value?: string) =>
  String(value ?? "")
    .trim()
    .toUpperCase();

const isAdditionalDocumentKind = (value?: string) =>
  normalizeDocumentKind(value) === ADDITIONAL_DOCUMENT_TYPE;

const isAdditionalDocumentEntry = (baseDoc: unknown, entry?: UploadEntry): boolean => {
  if (baseDoc && typeof baseDoc === "object" && !(baseDoc instanceof File)) {
    const candidate = baseDoc as PortServicesDocument;
    if (
      isAdditionalDocumentKind(candidate.type) ||
      isAdditionalDocumentKind(candidate.certificateType) ||
      normalizeDocumentKind(candidate.documentType).length > 0
    ) {
      return true;
    }
  }

  if (entry && !(entry instanceof File)) {
    const typedEntry = entry as UploadItem;
    if (normalizeDocumentKind(typedEntry.documentType).length > 0) {
      return true;
    }
  }

  return false;
};

const getExistingAdditionalDocumentName = (baseDoc: unknown): string => {
  if (baseDoc && typeof baseDoc === "object" && !(baseDoc instanceof File)) {
    return (baseDoc as PortServicesDocument).name ?? "";
  }

  return "";
};

const resolveAttachmentName = ({
  baseDoc,
  entry,
  fallbackIndex,
  certificateType,
  useCertificateTypeAsDocumentType = false,
}: {
  baseDoc?: Partial<PortServicesDocument>;
  entry?: UploadEntry;
  fallbackIndex: number;
  certificateType: string;
  useCertificateTypeAsDocumentType?: boolean;
}): string => {
  const typedEntry = entry as UploadItem | undefined;
  const urlFromEntry =
    (typedEntry?.url as string | undefined) ||
    (typedEntry as any)?.documentUrl ||
    (typeof baseDoc?.url === "string" ? baseDoc.url : undefined);

  const nameFromEntry =
    entry instanceof File
      ? entry.name
      : typedEntry?.name || typedEntry?.originalFileName || undefined;

  return resolveDocumentName({
    name: baseDoc?.name ?? nameFromEntry,
    originalFileName: baseDoc?.originalFileName ?? nameFromEntry,
    url: urlFromEntry,
    type: useCertificateTypeAsDocumentType
      ? (baseDoc?.certificateType ?? typedEntry?.certificateType ?? certificateType)
      : (baseDoc?.type ?? typedEntry?.certificateType ?? certificateType),
    fallback: `Document ${fallbackIndex + 1}`,
  });
};

const toMinimalUploadEntry = (entry: UploadEntry, fallbackName: string): UploadEntry => {
  if (entry instanceof File) return entry;

  const asObject = entry as UploadItem;
  const name =
    typeof asObject.name === "string" && asObject.name.trim().length ? asObject.name : fallbackName;

  return {
    name,
    url: asObject.url,
    type: asObject.type,
    certificateType: asObject.certificateType,
    originalFileName: asObject.originalFileName,
    documentType: asObject.documentType,
    otherSpecifyType: asObject.otherSpecifyType,
    documentSummary: asObject.documentSummary,
    issueDate: asObject.issueDate,
    expirationDate: asObject.expirationDate,
    idx: asObject.idx,
  };
};

const sanitizeUploadEntries = (entries: UploadEntry[]): UploadEntry[] =>
  entries.map((entry, idx) => {
    const fallbackName =
      typeof (entry as UploadItem)?.name === "string" && (entry as UploadItem).name.trim()
        ? (entry as UploadItem).name
        : `Document ${idx + 1}`;
    return toMinimalUploadEntry(entry, fallbackName);
  });

interface UsePortServicesDocumentUploadOptions {
  fieldName: string;
  certificateType: string;
  formData: PortServicesOutletContext["formData"];
  updateFormData: PortServicesOutletContext["updateFormData"];
}

interface UsePortServicesDocumentUploadResult {
  files: UploadEntry[];
  setFiles: (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) => void;
  handleFieldChangeForAzure: (fieldName: string, files: File[]) => Promise<void>;
  removeFileAtIndex: (index: number) => void;
  isUploading: boolean;
}

export const usePortServiceDocumentUpload = ({
  fieldName,
  certificateType,
  formData,
  updateFormData,
}: UsePortServicesDocumentUploadOptions): UsePortServicesDocumentUploadResult => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const usesCertificateTypeForDocumentKind = SHIP_TO_SHIP_DOCUMENT_FIELDS.has(fieldName);
  const shouldKeepAttachmentDocumentNameManual = fieldName === "attachmentDocuments";

  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const updateFilesForKey = useUploadStore((state) => state.updateFilesForKey);
  const files = useUploadStore((state) => state.filesByKey[fieldName] ?? EMPTY_UPLOAD_ENTRIES);
  const updateStoreFiles = useCallback(
    (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) => {
      if (typeof next === "function") {
        updateFilesForKey(fieldName, (prev: UploadEntry[]) =>
          sanitizeUploadEntries((next as (prev: UploadEntry[]) => UploadEntry[])(prev)),
        );
      } else {
        setFilesForKey(fieldName, sanitizeUploadEntries(next));
      }
    },
    [fieldName, setFilesForKey, updateFilesForKey],
  );

  /**
   * We keep the canonical attachmentDocuments array as PortServicesDocument[].
   * pendingValue is a buffer so we can first update RHF, then mirror into Zustand.
   */
  const [pendingValue, setPendingValue] = useState<PortServicesDocument[] | null>(null);

  const persistFormEntries = useCallback(
    (docs: PortServicesDocument[]) => {
      const safeDocs: PortServicesDocument[] = docs.map((doc, idx) => {
        const resolvedName = resolveAttachmentName({
          baseDoc: doc,
          fallbackIndex: idx,
          certificateType,
          useCertificateTypeAsDocumentType: usesCertificateTypeForDocumentKind,
        });

        const resolvedOriginal =
          (typeof doc.originalFileName === "string" && doc.originalFileName.trim()) || resolvedName;

        return {
          name: doc.name ?? "",
          ...(usesCertificateTypeForDocumentKind ? {} : { type: doc.type ?? certificateType }),
          url: doc.url ?? "",
          certificateType: usesCertificateTypeForDocumentKind
            ? (doc.certificateType ?? certificateType)
            : doc.certificateType,
          originalFileName: resolvedOriginal,
          expirationDate: doc.expirationDate ?? null,
          ...(doc.documentType !== undefined ? { documentType: doc.documentType } : {}),
          ...(doc.otherSpecifyType !== undefined ? { otherSpecifyType: doc.otherSpecifyType } : {}),
          ...(doc.documentSummary !== undefined ? { documentSummary: doc.documentSummary } : {}),
          ...(doc.issueDate !== undefined ? { issueDate: doc.issueDate ?? null } : {}),
        };
      });

      formData.setValue(fieldName as keyof PortServicesFormData, safeDocs as any, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });

      setPendingValue(safeDocs);
    },
    [certificateType, fieldName, formData, usesCertificateTypeForDocumentKind],
  );

  useEffect(() => {
    if (pendingValue) {
      // Mirror into Zustand form store
      updateFormData(fieldName as keyof PortServicesFormData, pendingValue);
      setPendingValue(null);
    }
  }, [fieldName, pendingValue, updateFormData]);

  /**
   * Update a single document entry (usually to set the blob URL after Azure upload).
   * We preserve name, expirationDate and originalFileName if already present.
   */
  const updateArray = useCallback(
    (item: { documentUrl: string; idx?: number }) => {
      const currentVal = formData.getValues(fieldName as keyof PortServicesFormData);
      const currentDocs: PortServicesDocument[] = Array.isArray(currentVal)
        ? (currentVal as PortServicesDocument[])
        : [];

      const storeEntries = useUploadStore.getState().getFilesForKey(fieldName) ?? [];

      const { documentUrl } = item;
      const idxFromResponse = typeof item.idx === "number" && item.idx >= 0 ? item.idx : -1;

      let targetIdx = idxFromResponse;

      if (targetIdx < 0 || targetIdx >= currentDocs.length) {
        const firstEmpty = currentDocs.findIndex(
          (doc) =>
            (!doc?.url || doc.url.trim().length === 0) &&
            (usesCertificateTypeForDocumentKind
              ? doc?.certificateType === certificateType || !doc?.certificateType
              : doc?.type === certificateType || !doc?.type),
        );
        targetIdx = firstEmpty === -1 ? currentDocs.length : firstEmpty;
      }

      const nextDocs = [...currentDocs];
      const entryForName = storeEntries[targetIdx] ?? storeEntries[storeEntries.length - 1];
      const baseDoc = nextDocs[targetIdx] ?? {};

      const resolvedName = resolveAttachmentName({
        baseDoc,
        entry: entryForName,
        fallbackIndex: targetIdx < 0 ? 0 : targetIdx,
        certificateType,
        useCertificateTypeAsDocumentType: usesCertificateTypeForDocumentKind,
      });

      const resolvedOriginal =
        (baseDoc as PortServicesDocument)?.originalFileName ??
        (entryForName instanceof File
          ? entryForName.name
          : ((entryForName as UploadItem | undefined)?.originalFileName ??
            (entryForName as UploadItem | undefined)?.name)) ??
        resolvedName;

      const resolvedDoc: PortServicesDocument = {
        name:
          shouldKeepAttachmentDocumentNameManual || isAdditionalDocumentEntry(baseDoc, entryForName)
            ? getExistingAdditionalDocumentName(baseDoc)
            : ((baseDoc as PortServicesDocument)?.name ?? ""),
        ...(usesCertificateTypeForDocumentKind ? {} : { type: certificateType }),
        url: documentUrl,
        certificateType: usesCertificateTypeForDocumentKind
          ? certificateType
          : baseDoc.certificateType,
        originalFileName: resolvedOriginal,
        expirationDate: (baseDoc as PortServicesDocument)?.expirationDate ?? null,
        ...((baseDoc as PortServicesDocument)?.documentType !== undefined
          ? { documentType: (baseDoc as PortServicesDocument).documentType }
          : {}),
        ...((baseDoc as PortServicesDocument)?.otherSpecifyType !== undefined
          ? { otherSpecifyType: (baseDoc as PortServicesDocument).otherSpecifyType }
          : {}),
        ...((baseDoc as PortServicesDocument)?.documentSummary !== undefined
          ? { documentSummary: (baseDoc as PortServicesDocument).documentSummary }
          : {}),
        ...((baseDoc as PortServicesDocument)?.issueDate !== undefined
          ? { issueDate: (baseDoc as PortServicesDocument).issueDate ?? null }
          : {}),
        ...((baseDoc as PortServicesDocument)?.inspectionDate !== undefined
          ? { inspectionDate: (baseDoc as PortServicesDocument).inspectionDate ?? null }
          : {}),
      };

      if (targetIdx >= nextDocs.length) {
        nextDocs.push(resolvedDoc);
      } else if (targetIdx >= 0) {
        nextDocs[targetIdx] = resolvedDoc;
      }

      persistFormEntries(nextDocs);

      // Also reflect the URL into uploadStore entries for UI consistency
      updateStoreFiles((prevEntries) => {
        const entries = [...prevEntries];

        const indexToUpdate =
          targetIdx === -1 ? entries.length - 1 : Math.min(targetIdx, entries.length - 1);

        if (indexToUpdate >= 0 && entries[indexToUpdate]) {
          const prevEntry = entries[indexToUpdate];
          entries[indexToUpdate] = toMinimalUploadEntry(
            {
              ...(prevEntry as any),
              name: resolvedName,
              url: documentUrl,
              type: usesCertificateTypeForDocumentKind
                ? undefined
                : (prevEntry as UploadItem | undefined)?.type,
              certificateType: usesCertificateTypeForDocumentKind
                ? certificateType
                : (prevEntry as UploadItem | undefined)?.certificateType,
              expirationDate: (prevEntry as UploadItem | undefined)?.expirationDate,
              ...((prevEntry as UploadItem | undefined)?.documentType !== undefined
                ? { documentType: (prevEntry as UploadItem).documentType }
                : {}),
              ...((prevEntry as UploadItem | undefined)?.otherSpecifyType !== undefined
                ? { otherSpecifyType: (prevEntry as UploadItem).otherSpecifyType }
                : {}),
              ...((prevEntry as UploadItem | undefined)?.documentSummary !== undefined
                ? { documentSummary: (prevEntry as UploadItem).documentSummary }
                : {}),
              ...((prevEntry as UploadItem | undefined)?.issueDate !== undefined
                ? { issueDate: (prevEntry as UploadItem).issueDate }
                : {}),
              ...((prevEntry as UploadItem | undefined)?.inspectionDate !== undefined
                ? { inspectionDate: (prevEntry as UploadItem).inspectionDate }
                : {}),
            },
            resolvedName,
          );
        }

        return entries;
      });
    },
    [
      certificateType,
      fieldName,
      formData,
      persistFormEntries,
      shouldKeepAttachmentDocumentNameManual,
      updateStoreFiles,
      usesCertificateTypeForDocumentKind,
    ],
  );

  const handleUploadError = useCallback(
    (error: Error) => {
      notify({
        title: t("security-clearance.notifications.sc-request-upload-error-title"),
        description: t("security-clearance.notifications.sc-request-upload-error-description"),
        type: "error",
      });
      console.error("Error on upload documents:", error);
    },
    [notify, t],
  );

  const handleUploadSuccess = useCallback(
    (
      dataArray: { documentUrl: string; certificateType: string }[],
      context: { certificateType: string; storeKey: string; idx?: number },
    ) => {
      if (!Array.isArray(dataArray)) return;
      const baseIdx = typeof context?.idx === "number" && context.idx >= 0 ? context.idx : 0;

      dataArray.forEach((data, offset) => {
        updateArray({ documentUrl: data.documentUrl, idx: baseIdx + offset });
      });
    },
    [updateArray],
  );

  const uploadDocumentsToAzure = useAzureBlobUpload({
    handleUploadSuccess,
    handleUploadError,
    transactionContext: {
      module: "vessel_call",
      screen: "PortServiceDocumentUpload",
      action: "upload_port_service_document",
      entityType: "port_service_document",
    },
  });

  /**
   * Build a PortServicesDocument[] view from the upload store + current form values.
   * This keeps user-entered name / expirationDate if they already exist.
   */
  const buildFormItemsFromStore = useCallback((): PortServicesDocument[] => {
    const storeEntries =
      useUploadStore.getState().getFilesForKey(fieldName) ?? EMPTY_UPLOAD_ENTRIES;

    const currentVal = formData.getValues(fieldName as keyof PortServicesFormData);
    const currentDocs: PortServicesDocument[] = Array.isArray(currentVal)
      ? (currentVal as PortServicesDocument[])
      : [];

    return storeEntries.map((entry, idx) => {
      const baseDoc = currentDocs[idx];
      const typedEntry = entry as UploadItem;

      const resolvedName = resolveAttachmentName({
        baseDoc,
        entry,
        fallbackIndex: idx,
        certificateType,
        useCertificateTypeAsDocumentType: usesCertificateTypeForDocumentKind,
      });

      const originalFileName =
        baseDoc?.originalFileName ??
        (entry instanceof File ? entry.name : typedEntry?.originalFileName || typedEntry?.name) ??
        resolvedName;

      const resolvedUrl = typedEntry?.url ?? (typedEntry as any)?.documentUrl ?? baseDoc?.url ?? "";

      const name =
        isAdditionalDocumentEntry(baseDoc, entry) || shouldKeepAttachmentDocumentNameManual
          ? getExistingAdditionalDocumentName(baseDoc)
          : (baseDoc?.name ?? "");

      return {
        name,
        ...(usesCertificateTypeForDocumentKind
          ? {}
          : { type: baseDoc?.type ?? typedEntry?.certificateType ?? certificateType }),
        url: resolvedUrl,
        certificateType: usesCertificateTypeForDocumentKind
          ? (baseDoc?.certificateType ?? typedEntry?.certificateType ?? certificateType)
          : baseDoc?.certificateType,
        originalFileName,
        expirationDate:
          (typedEntry?.expirationDate as Date | string | null | undefined) ??
          baseDoc?.expirationDate ??
          null,
        ...(baseDoc?.documentType !== undefined || typedEntry?.documentType !== undefined
          ? { documentType: baseDoc?.documentType ?? typedEntry?.documentType }
          : {}),
        ...(baseDoc?.otherSpecifyType !== undefined || typedEntry?.otherSpecifyType !== undefined
          ? { otherSpecifyType: baseDoc?.otherSpecifyType ?? typedEntry?.otherSpecifyType }
          : {}),
        ...(baseDoc?.documentSummary !== undefined || typedEntry?.documentSummary !== undefined
          ? { documentSummary: baseDoc?.documentSummary ?? typedEntry?.documentSummary }
          : {}),
        ...(baseDoc?.issueDate !== undefined || typedEntry?.issueDate !== undefined
          ? { issueDate: typedEntry?.issueDate ?? baseDoc?.issueDate ?? null }
          : {}),
        ...(baseDoc?.inspectionDate !== undefined || typedEntry?.inspectionDate !== undefined
          ? { inspectionDate: typedEntry?.inspectionDate ?? baseDoc?.inspectionDate ?? null }
          : {}),
      };
    });
  }, [
    certificateType,
    fieldName,
    formData,
    shouldKeepAttachmentDocumentNameManual,
    usesCertificateTypeForDocumentKind,
  ]);

  const removePendingFile = useCallback(
    (fileToRemove: File) => {
      // Remove the pending File entry from upload store
      updateStoreFiles((prev) =>
        prev.filter((entry) => !(entry instanceof File && entry === fileToRemove)),
      );
      // Rebuild attachmentDocuments from remaining store entries
      const updatedDocs = buildFormItemsFromStore();
      persistFormEntries(updatedDocs);
    },
    [buildFormItemsFromStore, persistFormEntries, updateStoreFiles],
  );

  const handleFieldChangeForAzure = useCallback(
    async (_fieldName: string, filesToUpload: File[]) => {
      const incomingFiles = Array.isArray(filesToUpload)
        ? filesToUpload.filter((entry): entry is File => entry instanceof File)
        : [];

      // Sync current uploadStore entries into attachmentDocuments
      const formEntries = buildFormItemsFromStore();
      persistFormEntries(formEntries);

      if (incomingFiles.length === 0) return;

      const startIdx = Math.max(formEntries.length - incomingFiles.length, 0);

      for (const [idx, file] of incomingFiles.entries()) {
        const payloadForSasTokenActionWrite = {
          prefix: SasTokenPrefix.PORT_SERVICES,
          contentType: file.type || null,
          privilege: SasTokenPrivilege.WRITE,
          blobUrl: null,
        };

        let sasTokenResponse: Awaited<ReturnType<typeof getSasToken>>;
        try {
          sasTokenResponse = await getSasToken(payloadForSasTokenActionWrite);
        } catch (error) {
          const normalizedError =
            error instanceof Error ? error : new Error("Failed to obtain SAS token from API.");
          handleUploadError(normalizedError);
          removePendingFile(file);
          continue;
        }

        try {
          await uploadDocumentsToAzure.mutateAsync({
            file,
            blobUrl: sasTokenResponse.blobUrl,
            sasToken: sasTokenResponse.sasToken,
            certificateType,
            storeKey: fieldName,
            idx: startIdx + idx,
          });
        } catch (error) {
          const normalizedError =
            error instanceof Error ? error : new Error("Failed to upload document to Azure.");
          handleUploadError(normalizedError);
          removePendingFile(file);
        }
      }
    },
    [
      buildFormItemsFromStore,
      certificateType,
      fieldName,
      handleUploadError,
      persistFormEntries,
      removePendingFile,
      uploadDocumentsToAzure,
    ],
  );

  const removeFileAtIndex = useCallback(
    (index: number) => {
      const currentVal = formData.getValues(fieldName as keyof PortServicesFormData);
      const currentDocs: PortServicesDocument[] = Array.isArray(currentVal)
        ? (currentVal as PortServicesDocument[])
        : [];

      const updatedDocs = currentDocs.filter((_, i) => i !== index);

      // Update form
      persistFormEntries(updatedDocs);
    },
    [fieldName, formData, persistFormEntries],
  );

  return {
    files,
    setFiles: updateStoreFiles,
    handleFieldChangeForAzure,
    removeFileAtIndex,
    isUploading: uploadDocumentsToAzure.isPending,
  };
};

export const hasOperationsApproved = (flowConfig: any): boolean => {
  if (!flowConfig) {
    return false;
  }
  return Object.values(flowConfig).some((status: any) => status.statusesList?.OPERATIONS_APPROVED);
};
